import { useTranslation } from 'react-i18next';
import {
  ACCOUNT_WORKFLOW_STATUSES,
  AccountWorkflowStatus,
  AccountStatusCounts,
  ReportedAccountStatus,
  withStyles,
} from 'vopay';
import StatusSummaryCard from '@/components/Entities/UI/StatusSummaryCard/StatusSummaryCard';
import type { PartnerAccountStatusFilter } from '@/components/Entities/hooks/useApi/useFetchPartnerAccounts';
import { TERMINAL_STATUSES } from './onboardingPipeline';
import styles from './AccountsOverview.module.scss';

const cn = withStyles(styles);

/** The card's "in onboarding" figure counts everything but these. */
const TERMINAL_STATUS_SET: ReadonlySet<string> = new Set<string>(TERMINAL_STATUSES);

/** Membership test for "is this a status this frontend knows about", over the whole vocabulary. */
const KNOWN_STATUSES: ReadonlySet<string> = new Set<string>(ACCOUNT_WORKFLOW_STATUSES);

type AccountStatusSummaryCardProps = {
  fetching: boolean;
  numTotalAccounts: number;
  /**
   * Partial while `fetching`; every documented status is present once the summary has responded.
   * The backend keys these off the live `VIOAccounts.AccountStatus` enum, so the response can also
   * carry a status newer than `AccountWorkflowStatus` — see `unrecognizedStages` below.
   */
  statusCounts: Partial<AccountStatusCounts>;
  /**
   * The tiles, in order (see `getAccountStatusTiles`): the partner's pipeline, other populated
   * onboarding statuses, then the terminal statuses. The same list drives the list's Status filter.
   */
  stages: AccountWorkflowStatus[];
  /** The status the list is filtered to; its tile renders selected. Empty (or 'all') = no filter. */
  selectedStatus: PartnerAccountStatusFilter;
  /** A tile click (ID-13981): the host filters the list to the status, or clears it when it is the selected one. */
  onStatusClick: (status: ReportedAccountStatus) => void;
};

export default function AccountStatusSummaryCard({
  fetching,
  numTotalAccounts,
  statusCounts,
  stages,
  selectedStatus,
  onStatusClick,
}: AccountStatusSummaryCardProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountsOverview' });

  // Widened only to read keys the union does not name. Everything derived from the vocabulary below
  // stays typed; this is the one place the raw response shape is acknowledged.
  const counts: Record<string, number | undefined> = statusCounts;

  /**
   * Statuses the response carried that this frontend has no name for yet — a status added to the
   * backend enum before `AccountWorkflowStatus` catches up. They are deliberately kept out of the
   * typed lists: the label resolver renders them from their raw value, and showing them is what
   * keeps the card's promise that a stage holding accounts is never hidden.
   */
  const unrecognizedStages: ReportedAccountStatus[] = Object.keys(counts).filter(
    (status) => !KNOWN_STATUSES.has(status) && (counts[status] ?? 0) > 0,
  );

  // Only one status can filter the list at a time, so the tiles are a radio group (ID-13981).
  return (
    <StatusSummaryCard<ReportedAccountStatus>
      fetching={fetching}
      numTotal={numTotalAccounts}
      totalLabel={t('accounts', { defaultValue: 'Accounts' })}
      statusCounts={counts}
      terminalStatuses={TERMINAL_STATUS_SET}
      stages={[...stages, ...unrecognizedStages]}
      onStatusClick={onStatusClick}
      selectedStatus={selectedStatus === 'all' ? '' : selectedStatus}
      groupLabel={t('statusFilterLabel', { defaultValue: 'Filter accounts by status' })}
      className={cn('summaryCard')}
      data-testid="accountStatusSummaryCard"
    />
  );
}
